import React from 'react';
import { Label } from '@patternfly/react-core';
import { formatPrice, formatRecurring } from '../../../utils/money';
import { formatBareDateNumeric } from '../../../utils/dateFormat';

// Shared display helpers for Special deals, used by the Agreements page and the VM overview's
// attached-deal rows so kind badges and value formatting stay identical on both surfaces.

/** Kind badge: `+ charge` / `− rebate` / `− % rebate` (values are stored positive; the sign is the kind's). */
export function renderDealKindBadge(kind: string): React.ReactNode {
  switch (kind) {
    case 'charge':
      return (
        <Label color="blue" isCompact>
          + charge
        </Label>
      );
    case 'discount_fixed':
      return (
        <Label color="green" isCompact>
          − rebate
        </Label>
      );
    case 'discount_percent':
      return (
        <Label color="green" isCompact>
          − % rebate
        </Label>
      );
    default:
      // Forward-compatible fallback for a kind this build doesn't know yet.
      return (
        <Label color="grey" isCompact>
          {kind}
        </Label>
      );
  }
}

const PERCENT_FORMATTER = new Intl.NumberFormat('fr-BE', { maximumFractionDigits: 2 });

/** Render a deal's nominal value humanely: `300,00 €` for money kinds, `20 %` for percent rebates. */
export function formatDealValue(kind: string, value: string): string {
  if (kind === 'discount_percent') {
    const n = parseFloat(value);
    return Number.isNaN(n) ? `${value} %` : `${PERCENT_FORMATTER.format(n)} %`;
  }
  return formatPrice(value);
}

/** Cadence cell: `Monthly · next 19/08/2026` when an anchor exists, else just the cadence. */
export function formatDealCadence(recurring: string | null, nextBillingAt: string | null): string {
  const cadence = recurring === null ? '—' : formatRecurring(recurring);
  return nextBillingAt !== null ? `${cadence} · next ${formatBareDateNumeric(nextBillingAt)}` : cadence;
}

/** Remaining-occurrences cell: `∞` when unlimited (null), `0 (spent)` when exhausted. */
export function formatOccurrencesLeft(occurrencesLeft: number | null): string {
  if (occurrencesLeft === null) {
    return '∞';
  }
  return occurrencesLeft === 0 ? '0 (spent)' : String(occurrencesLeft);
}
